<template>
  <div class="project-messages-panel">
    <p
      v-if="errorText"
      class="project-messages-panel__error mb-2"
    >
      {{ errorText }}
    </p>

    <MessageThread
      :messages="localMessages"
      :isSending="isSending"
      :meAvatar="meAvatar"
      :readOnly="readOnly"
      :submitOnEnter="true"
      placeholder="Comment..."
      @send="sendMessage"
      @like="onLike"
      @unlike="onUnlike"
    />
  </div>
</template>

<script>
import MatchmakingService from '@/services/MatchmakingService';
import MessageThread from '@/components/matchmaking/MessageThread.vue';

export default {
  name: 'ProjectMessagesPanel',

  components: {
    MessageThread,
  },

  props: {
    projectId: {
      type: [Number, String],
      required: true,
    },
    messages: {
      type: Array,
      default: () => [],
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      localMessages: [],
      isSending: false,
      errorText: '',
    };
  },

  computed: {
    me() {
      return this.$store.getters.getMe || {};
    },

    meAvatar() {
      return this.me?.avatar || this.me?.photo || '';
    },
  },

  watch: {
    messages: {
      immediate: true,
      handler(val) {
        this.localMessages = Array.isArray(val) ? [...val] : [];
      },
    },
  },

  methods: {
    async sendMessage(text) {
      if (!text || this.readOnly) return;

      this.errorText = '';
      this.isSending = true;
      try {
        const payload = {
          project_id: this.projectId,
          text,
        };

        const { data } = await MatchmakingService.marketplaceProjectMessage(payload);

        if (data && typeof data === 'object' && data.id != null) {
          const message = this.withCurrentUserRole(data);
          this.localMessages.push(message);
          this.$emit('sent', message);
        } else {
          const backendMessage = data && typeof data === 'object' && data.message
            ? String(data.message)
            : 'Failed to send message. Please try again.';
          this.errorText = backendMessage;
        }
      } catch (e) {
        const backendMessage = e && e.response && e.response.data && e.response.data.message
          ? String(e.response.data.message)
          : 'Failed to send message. Please try again.';
        this.errorText = backendMessage;
        // eslint-disable-next-line no-console
        console.error('Project message send failed:', e);
      } finally {
        this.isSending = false;
      }
    },

    withCurrentUserRole(message) {
      const role = message?.user?.role || message?.role;
      if (role) return message;

      const fallbackRole = this.me?.role || (this.me?.safe_seller ? 'seller' : 'buyer');
      return {
        ...message,
        user: {
          ...(message.user || {}),
          role: fallbackRole,
        },
      };
    },

    findLocalMessage(msg) {
      const id = msg && msg.id;
      // eslint-disable-next-line no-underscore-dangle
      const key = msg && msg._key;
      return this.localMessages.find((x) => {
        if (id != null && x?.id === id) return true;
        // eslint-disable-next-line no-underscore-dangle
        if (key != null && x?._key === key) return true;
        return false;
      });
    },

    applyLikeChange(msg, delta) {
      const m = this.findLocalMessage(msg);
      if (!m) return;
      const liked = delta > 0;
      this.$set(m, 'liked', liked);
      this.$set(m, 'is_liked', liked);
      const current = Number(m?.likes_count ?? m?.likes ?? m?.like ?? 0) || 0;
      const next = Math.max(0, current + delta);
      this.$set(m, 'likes_count', next);
      this.$set(m, 'likes', next);
      this.$set(m, 'like', next);
    },

    async onLike(msg) {
      if (!msg?.id) return;
      try {
        await MatchmakingService.addLikeToMessage(msg.id);
        this.applyLikeChange(msg, 1);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Like failed:', e);
      }
    },

    async onUnlike(msg) {
      if (!msg?.id) return;
      try {
        await MatchmakingService.removeLikeFromMessage(msg.id);
        this.applyLikeChange(msg, -1);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Unlike failed:', e);
      }
    },
  },
};
</script>

<style scoped>
.project-messages-panel__error {
  color: #960c15;
}
</style>
